import { Suspense, useEffect, useRef, useState, type MouseEvent } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { openComposePane, toggleComposePane, useComposePaneOpen } from '@/lib/compose-pane';
import { BellIcon, BrusukonIcon, ComposeIcon, HomeIcon, PersonIcon, ScrollIcon, SearchIcon, SettingsIcon } from './icons';
import { useCompose } from './compose-modal';
import { composeFabAllowedOnPath } from '@/lib/compose-fab';
import { useSession } from '@/lib/session';
import { getUnreadNotificationCount } from '@/lib/atproto';
import { useVisibleColumn } from '@/lib/visible-column';
import { removeSplash } from '@/lib/splash';
import { useQuestActionableCount, computeQuestActionableCount, setQuestActionableCount } from '@/lib/quest-actionable';
import { subscribeNotificationsSeen } from '@/lib/notification-seen';
import { useRuntimeConfig } from '@/components/config-provider';
import type { AppColumnKind } from '@/lib/app-columns';

/** footer-nav の各タブと workspace カラム kind の対応。
 *  workspace 表示中にタブを押したとき、該当 kind のカラムがあれば
 *  ページ遷移せずそこへ横スクロールする (TweetDeck 的挙動)。 */
const nav: Array<{
  to: string;
  label: string;
  icon: typeof HomeIcon;
  end?: boolean;
  key: string;
  columnKind?: AppColumnKind;
}> = [
  { to: '/', label: 'ホーム', icon: HomeIcon, end: true, key: 'home', columnKind: 'home' },
  { to: '/me', label: '自分', icon: PersonIcon, key: 'me' },
  { to: '/board', label: 'クエスト', icon: ScrollIcon, key: 'quests', columnKind: 'board' },
  { to: '/notifications', label: '通知', icon: BellIcon, key: 'notifications', columnKind: 'notifications' },
  { to: '/spirit', label: 'ブルスコン', icon: BrusukonIcon, key: 'spirit' },
  { to: '/search', label: '検索', icon: SearchIcon, key: 'search', columnKind: 'search' },
  { to: '/settings', label: '設定', icon: SettingsIcon, key: 'settings' },
];

/** 未ログイン時の footer-nav。ホーム/通知/検索などログイン必須の項目は出さず、
 *  閲覧できるクエスト掲示板とログイン導線だけにする。 */
const navLoggedOut: typeof nav = [
  { to: '/board', label: 'クエスト掲示板', icon: ScrollIcon, key: 'quests' },
  { to: '/onboarding', label: 'ログイン', icon: PersonIcon, key: 'login' },
];

export function AppShell() {
  const session = useSession();
  const { openCompose } = useCompose();
  const location = useLocation();
  const navigate = useNavigate();
  const composeOpen = useComposePaneOpen();
  const [unread, setUnread] = useState(0);
  // 自分のアクション待ちクエスト件数 (承認待ち + 完了報告待ち)。クエストタブに赤カウント表示。
  const questActionable = useQuestActionableCount();
  const runtimeConfig = useRuntimeConfig();

  // セッション復元が終わって実コンテンツを描画できる状態になったら、即時スプラッシュを
  // 滑らかに除去する (JS パース + 復元の間は 1 枚のまま維持し「準備しています…」を挟まない)。
  useEffect(() => {
    if (session.status !== 'loading') removeSplash();
  }, [session.status]);
  const visibleKind = useVisibleColumn();
  const headerRef = useRef<HTMLElement>(null);
  const footerRef = useRef<HTMLElement>(null);

  // header + footer-nav の実高 (margin 込み) を CSS 変数
  // --shell-chrome-height に書き出す。workspace のカラム高
  // calc(100dvh - var(--shell-chrome-height)) がマジックナンバーでなく
  // 実測に追従する (文字サイズ設定で chrome の高さが変わっても崩れない)。
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const outerHeight = (el: HTMLElement | null): number => {
      if (!el) return 0;
      const r = el.getBoundingClientRect();
      const cs = getComputedStyle(el);
      // footer-nav は margin-bottom + sticky bottom offset を持つので
      // margin も含めて占有高を測る
      return r.height + parseFloat(cs.marginTop || '0') + parseFloat(cs.marginBottom || '0');
    };
    const update = () => {
      const header = outerHeight(headerRef.current);
      const footer = outerHeight(footerRef.current);
      document.documentElement.style.setProperty('--shell-chrome-height', `${Math.ceil(header + footer)}px`);
      // footer 単独の占有高も書き出す (compose FAB の bottom 位置を header≒footer
      // 仮定に頼らず footer 実測に追従させる)。
      document.documentElement.style.setProperty('--footer-height', `${Math.ceil(footer)}px`);
      // header 単独の高さ。PC では footer が「左の縦レール」になり縦方向の chrome に
      // 含めないため、カラム高は 100dvh - header だけで計算する (--header-height を使う)。
      document.documentElement.style.setProperty('--header-height', `${Math.ceil(header)}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    if (headerRef.current) ro.observe(headerRef.current);
    if (footerRef.current) ro.observe(footerRef.current);
    return () => ro.disconnect();
  }, []);

  // 60 秒ごとに未読数をポーリング。サインイン状態が変わったら即時取得。
  // 通知タブを開いた瞬間に local で 0 にリセットし、サーバーも追随する。
  useEffect(() => {
    if (session.status !== 'signed-in' || !session.agent) {
      setUnread(0);
      return;
    }
    const agent = session.agent;
    let cancelled = false;
    const tick = () => {
      void getUnreadNotificationCount(agent).then((n) => {
        if (!cancelled) setUnread(n);
      });
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [session.status, session.agent]);

  // クエストのアクション待ち件数をルート非依存で集計 (board カラムが mount されない
  // /me 等に直接着地してもバッジが出るように)。サインイン時 + 復帰(focus)時に再計算し、
  // サインアウトで 0 リセット。focus は throttle し、index は getQuestIndexCached で
  // board カラムと共有するので、連続発火しても重い discovery を繰り返さない。
  const directoryKey = runtimeConfig.directory.map((u) => u.did).join(',');
  useEffect(() => {
    if (session.status !== 'signed-in' || !session.agent || !session.did) {
      setQuestActionableCount(0);
      return;
    }
    const agent = session.agent;
    const did = session.did;
    const dids = directoryKey ? (directoryKey.split(',') as Parameters<typeof computeQuestActionableCount>[2]) : [];
    let cancelled = false;
    let lastRun = 0;
    const run = () => {
      const now = Date.now();
      if (now - lastRun < 60_000) return; // 復帰連打を間引く
      lastRun = now;
      void computeQuestActionableCount(agent, did, dids)
        .then((n) => { if (!cancelled) setQuestActionableCount(n); })
        .catch(() => {});
    };
    run();
    const onVisible = () => { if (document.visibilityState === 'visible') run(); };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [session.status, session.agent, session.did, directoryKey]);

  // 通知タブ (ルート) を開いた瞬間にバッジを消す
  useEffect(() => {
    if (location.pathname === '/notifications' && unread > 0) {
      setUnread(0);
    }
  }, [location.pathname, unread]);

  // 通知を既読化した瞬間 (NotificationsFeed が updateSeen を撃ったとき) にバッジを消す。
  // モバイルはカラム (swipe, pathname='/') で通知を見るのでルート判定だけでは消えず、
  // 60 秒 poll まで赤ポッチが残っていた。既読化イベントに直結させて即消す。
  useEffect(() => subscribeNotificationsSeen(() => setUnread(0)), []);

  const onWorkspace = location.pathname === '/';
  // 投稿 FAB は、入力フォーム/認証系などタイムラインでない画面では出さない
  // (送信ボタン等と被って文脈ノイズになるため)。
  const showComposeFab =
    session.status === 'signed-in' && composeFabAllowedOnPath(location.pathname);
  // workspace 中の active 表示用 kind。IntersectionObserver 発火前
  // (visibleKind=null) は先頭 = home を仮の active にしてチラつきを防ぐ。
  const activeWorkspaceKind: AppColumnKind | null = onWorkspace ? (visibleKind ?? 'home') : null;

  /** workspace 表示中: 該当 kind のカラムが存在すればページ遷移せず
   *  そこへ横スクロールする。カラムが無ければ先頭カラムへ。 */
  function navClick(e: MouseEvent, columnKind?: AppColumnKind) {
    if (!onWorkspace || !columnKind) return;
    e.preventDefault();
    // カラム内ドリルダウンの詳細が開いている状態で、見ているカラムのタブを再タップ
    // したら 1 つ戻す (= 詳細を閉じる)。navigate('/') で state を全消しすると他カラムの
    // ドリルまで閉じ (M1)、かつ push なので Back で開き直ってしまう (M2)。history を
    // pop する navigate(-1) なら「← 戻る」/端末バックと一貫する。
    const stack = (location.state as { colStack?: unknown[] } | null)?.colStack;
    if (Array.isArray(stack) && stack.length > 0 && columnKind === activeWorkspaceKind) {
      navigate(-1);
      return;
    }
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const behavior: ScrollBehavior = reduce ? 'auto' : 'smooth';
    const el =
      document.querySelector(`[data-column-kind="${columnKind}"]`) ??
      // 該当カラムを削除済みでも「ホーム」等で迷子にならないよう先頭カラムへ
      document.querySelector('.workspace-column');
    el?.scrollIntoView({ behavior, inline: 'start', block: 'nearest' });
    // 「今見ているカラムのタブをもう一度タップ」したときだけ、カラム内の縦スクロールを
    // 最上部へ戻す (ホーム再タップで TL 先頭に戻る一般的な挙動)。別カラムへ移動する
    // だけの初回タップでは縦位置をリセットせず、前に見ていた位置を保持する。
    // 縦スクロール要素は .workspace-column-body (VirtualFeed の scrollParent)。
    if (columnKind === activeWorkspaceKind) {
      el?.querySelector('.workspace-column-body')?.scrollTo({ top: 0, behavior });
    }
  }

  return (
    <div className="app-shell">
      <header className="header" ref={headerRef}>
        <strong>あおぞらくえすと</strong>
      </header>
      <main className="content">
        {/* ルートは lazy 分割 (main.tsx)。チャンク取得中の簡素なフォールバック。 */}
        <Suspense fallback={<div style={{ padding: '1em', fontSize: '0.85em', color: 'var(--color-muted)' }}>読み込み中…</div>}>
          <Outlet />
        </Suspense>
      </main>
      {showComposeFab && (
        <button
          type="button"
          className="compose-fab"
          aria-label="投稿する"
          onClick={() => openCompose()}
        >
          <ComposeIcon size={24} />
        </button>
      )}
      <nav className="footer-nav" ref={footerRef}>
        {/* 投稿ボタン: PC では左ナビレールの先頭に置いて押しやすく (CSS で PC のみ表示)。
            モバイルは従来どおり右下の compose-fab を使う。表示可否は FAB と同じ
            (composeFabAllowedOnPath = フォーム/認証画面では出さない) に揃える。 */}
        {showComposeFab && (
          <button
            type="button"
            className={`footer-nav-item footer-nav-compose${composeOpen ? ' active' : ''}`}
            aria-label="投稿する"
            aria-pressed={composeOpen}
            title="投稿する"
            onClick={() => {
              // 投稿カラムは workspace ('/') が描画する。別ルートに居たら '/' へ移って開く。
              // workspace に居れば開閉トグル。
              if (location.pathname !== '/') {
                navigate('/');
                openComposePane();
              } else {
                toggleComposePane();
              }
            }}
          >
            <ComposeIcon size={26} />
          </button>
        )}
        {(session.status === 'signed-in' ? nav : navLoggedOut).map(({ to, label, icon: Icon, end, key, columnKind }) => (
          <NavLink
            key={to}
            to={to}
            end={end ?? false}
            aria-label={label}
            title={label}
            onClick={(e) => navClick(e, columnKind)}
            className={({ isActive }) => {
              // workspace 表示中は「いま見えているカラムの kind」を active に
              // 反映する (モバイル横スワイプで位置が分かる)。
              // columnKind を持たないタブ (自分/ブルスコン/設定) は従来通り
              // isActive で判定する。
              const active = activeWorkspaceKind !== null && columnKind !== undefined
                ? columnKind === activeWorkspaceKind
                : isActive;
              return `footer-nav-item${active ? ' active' : ''}`;
            }}
          >
            {/* バッジはアイコン基準で配置する。footer-nav はモバイルでは横長タブ、PC では
                幅 56px の縦レール (44x44 の正方形タブ) になるため、タブ基準 (calc(50%-..))
                だとレールで位置が破綻する。アイコン右上に固定すれば両レイアウトで成立する。 */}
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              <Icon size={26} />
              {key === 'notifications' && unread > 0 && (
                <span
                  aria-label={`未読 ${unread} 件`}
                  style={{
                    position: 'absolute',
                    top: -3,
                    right: -4,
                    width: 10,
                    height: 10,
                    borderRadius: 5,
                    background: 'var(--color-notify-badge)',
                    boxShadow: '0 0 0 2px var(--color-bg, #000)',
                  }}
                />
              )}
              {key === 'quests' && session.status === 'signed-in' && questActionable > 0 && (
                // 自分のアクション待ち (承認 / 完了報告) 件数の赤カウントバッジ。
                // @mention を見逃しても未対応が残っていれば常に見える。
                <span
                  aria-label={`対応待ちクエスト ${questActionable} 件`}
                  style={{
                    position: 'absolute',
                    top: -6,
                    right: -9,
                    minWidth: 16,
                    height: 16,
                    padding: '0 4px',
                    boxSizing: 'border-box',
                    borderRadius: 8,
                    background: 'var(--color-notify-badge)',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                    lineHeight: '16px',
                    textAlign: 'center',
                    boxShadow: '0 0 0 2px var(--color-bg, #000)',
                  }}
                >
                  {questActionable > 9 ? '9+' : questActionable}
                </span>
              )}
            </span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
